import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { UserPlus, Lightbulb, Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            <div className="w-20 h-20 bg-[var(--obsidian)] border-2 border-[var(--border-default)] flex items-center justify-center">
              <Lightbulb size={40} className="text-[#FFD60A]" />
            </div>
          </div>
          <h1 className="text-4xl font-black tracking-tighter mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            VAULT
          </h1>
          <p className="text-sm text-[var(--text-secondary)] font-mono uppercase tracking-wider">
            Idea Storage
          </p>
        </div>

        {/* Register Form */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Create Account
          </h2>

          {error && (
            <div className="bg-[#FF3B30]/10 border-2 border-[#FF3B30] p-4 mb-6">
              <p className="text-sm text-[#FF3B30] font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Name
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                style={{ borderRadius: 0 }}
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                style={{ borderRadius: 0 }}
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] px-4 py-3 pr-12 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  style={{ borderRadius: 0 }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-[var(--text-secondary)] mt-1">At least 6 characters</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--primary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <UserPlus size={20} />
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
            <p className="text-xs text-[var(--text-secondary)] text-center mt-3">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-[var(--obsidian)]">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="underline hover:text-[var(--obsidian)]">Privacy Policy</Link>.
            </p>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[var(--text-secondary)]">
              Already have an account?{' '}
              <Link to="/login" className="text-[var(--obsidian)] font-bold hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;